import { airlineLabel, extractIataCode } from './airportCodes';
import { seededRandomPrice } from './pricing';

const AIRLINE_CODES = ['AA', 'UA', 'DL', 'BA', 'AF', 'LH', 'EK', 'B6'];
const HOTEL_NAMES = ['Grand Vista', 'Harbor Inn', 'City Lights Hotel', 'Sunset Suites', 'Metro Stay'];
const CAR_TYPES = ['Economy', 'Compact', 'SUV', 'Luxury', 'Van'];
const CRUISE_LINES = ['Ocean Pearl', 'Star Horizon', 'Royal Wave', 'Blue Meridian'];

function hash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) h = (h << 5) - h + str.charCodeAt(i);
  return Math.abs(h);
}

export function generateFlightResults(params) {
  const {
    from = 'New York',
    to = 'Miami',
    trip = 'roundtrip',
    passengers = '1',
    return: returnDate = '',
  } = params;
  const isRoundTrip = trip !== 'oneway';
  const seed = hash(`${from}-${to}-${trip}-${returnDate}`);
  const count = 12 + (seed % 5);
  const pax = parseInt(passengers, 10) || 1;

  return Array.from({ length: count }, (_, i) => {
    const price = seededRandomPrice(`${seed}-${i}`);
    const stops = i % 3 === 0 ? 0 : i % 3 === 1 ? 1 : 2;
    const returnStops = i % 4 === 0 ? 0 : 1;
    const departHour = 6 + ((seed + i * 3) % 14);
    const code = AIRLINE_CODES[(seed + i) % AIRLINE_CODES.length];
    const cabinKey = i < 2 ? 'ECONOMY' : i < 5 ? 'PREMIUM_ECONOMY' : 'BUSINESS';
    const durMin = (2 + stops) * 60 + (20 + (i * 5) % 40);
    const returnDurMin = (2 + returnStops) * 60 + (25 + (i * 4) % 35);
    const departMin = i % 2 === 0 ? 30 : 15;
    const arriveHour = (departHour + 2 + stops) % 24;
    const departAt = `2026-06-15T${String(departHour).padStart(2, '0')}:${String(departMin).padStart(2, '0')}:00`;

    const flight = {
      id: `fl-${seed}-${i}`,
      airline: airlineLabel(code),
      airlineCode: code,
      from,
      to,
      fromCode: extractIataCode(from),
      toCode: extractIataCode(to),
      depart: `${String(departHour).padStart(2, '0')}:${String(departMin).padStart(2, '0')}`,
      arrive: `${String(arriveHour).padStart(2, '0')}:${i % 2 === 0 ? '45' : '20'}`,
      departAt,
      duration: `${2 + stops}h ${20 + (i * 5) % 40}m`,
      durationMinutes: durMin,
      stops,
      price: Math.round(price * pax * 100) / 100,
      perPerson: price,
      class: cabinKey.replace('_', ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()),
      cabin: cabinKey,
      currency: 'USD',
      source: 'demo',
      isRoundTrip: false,
    };

    if (isRoundTrip) {
      const returnDepartHour = 10 + ((seed + i * 2) % 10);
      const returnArriveHour = (returnDepartHour + 2 + returnStops) % 24;
      flight.isRoundTrip = true;
      flight.returnDepart = `${String(returnDepartHour).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`;
      flight.returnArrive = `${String(returnArriveHour).padStart(2, '0')}:${i % 2 === 0 ? '50' : '15'}`;
      flight.returnDepartAt = returnDate
        ? `${returnDate}T${String(returnDepartHour).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}:00`
        : `2026-06-22T${String(returnDepartHour).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}:00`;
      flight.returnDuration = `${2 + returnStops}h ${25 + (i * 4) % 35}m`;
      flight.returnDurationMinutes = returnDurMin;
      flight.returnStops = returnStops;
    }

    return flight;
  }).sort((a, b) => a.price - b.price);
}

export function generateHotelResults(params) {
  const { destination = 'Paris', guests = '2', rooms = '1' } = params;
  const seed = hash(destination);
  const guestNum = parseInt(guests, 10) || 2;
  const roomNum = parseInt(rooms, 10) || 1;

  return Array.from({ length: 10 }, (_, i) => {
    const stars = 3 + (i % 3);
    const perNight = seededRandomPrice(`${seed}-hotel-${i}`);
    const price = perNight * roomNum;
    return {
      id: `ht-${seed}-${i}`,
      name: `${HOTEL_NAMES[(seed + i) % HOTEL_NAMES.length]} ${destination}`,
      destination,
      stars,
      rating: (3.8 + (i % 12) / 10).toFixed(1),
      amenities: ['Free WiFi', 'Breakfast', 'Pool', 'Gym', 'Parking'].slice(0, 3 + (i % 3)),
      price,
      perNight,
      guests: guestNum,
      rooms: roomNum,
    };
  }).sort((a, b) => a.price - b.price);
}

export function generateCarResults(params) {
  const { pickup = 'Los Angeles Airport', carType = 'any' } = params;
  const seed = hash(`${pickup}-${carType}`);

  return Array.from({ length: 8 }, (_, i) => {
    const type = carType === 'any' ? CAR_TYPES[i % CAR_TYPES.length] : carType;
    const perDay = seededRandomPrice(`${seed}-car-${i}`);
    return {
      id: `cr-${seed}-${i}`,
      type,
      company: ['Hertz', 'Avis', 'Enterprise', 'Budget'][(seed + i) % 4],
      pickup,
      seats: type === 'Van' ? 7 : type === 'SUV' ? 5 : 4,
      transmission: 'Automatic',
      price: perDay,
      perDay,
    };
  }).sort((a, b) => a.price - b.price);
}

export function generatePackageResults(params) {
  const { destination = 'Orlando', travelers = '2' } = params;
  const seed = hash(destination);
  const t = parseInt(travelers, 10) || 2;

  return Array.from({ length: 6 }, (_, i) => {
    const perPerson = seededRandomPrice(`${seed}-pkg-${i}`);
    const price = perPerson * t;
    return {
      id: `pk-${seed}-${i}`,
      title: `${destination} Getaway ${i + 3}N/${i + 4}D`,
      destination,
      includes: ['Flights', 'Hotel', 'Transfers'].slice(0, 2 + (i % 2)),
      nights: i + 3,
      price,
      perPerson,
    };
  }).sort((a, b) => a.price - b.price);
}

export function generateCruiseResults(params) {
  const { port = 'Miami', destination = 'Caribbean' } = params;
  const seed = hash(`${port}-${destination}`);

  return Array.from({ length: 7 }, (_, i) => {
    const nights = 5 + i;
    const price = seededRandomPrice(`${seed}-cruise-${i}`);
    return {
      id: `cu-${seed}-${i}`,
      line: CRUISE_LINES[(seed + i) % CRUISE_LINES.length],
      port,
      destination,
      nights,
      departDate: `2026-0${6 + (i % 4)}-${10 + i}`,
      price,
      cabin: ['Interior', 'Ocean View', 'Balcony', 'Suite'][i % 4],
    };
  }).sort((a, b) => a.price - b.price);
}
